import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const colorStyles = {
  blue:   'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
  green:  'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400',
  purple: 'bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400',
  orange: 'bg-orange-50 text-orange-500 dark:bg-orange-950/40 dark:text-orange-400',
  teal:   'bg-teal-50 text-teal-600 dark:bg-teal-950/40 dark:text-teal-400',
} as const;

export type StripColor = keyof typeof colorStyles;

export interface StripStat {
  icon: LucideIcon;
  value: string | number;
  label: string;
  color: StripColor;
}

interface StatsStripProps {
  stats: StripStat[];
  isLoading?: boolean;
}

function StatItem({ stat }: { stat: StripStat }) {
  return (
    <div className="flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-3 min-w-[140px] shrink-0">
      <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', colorStyles[stat.color])}>
        <stat.icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-bold text-foreground tracking-tight leading-none">
          {stat.value}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">{stat.label}</div>
      </div>
    </div>
  );
}

function StatItemSkeleton() {
  return (
    <div className="bg-card border border-border rounded-xl px-4 py-3 min-w-[140px] shrink-0 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-muted" />
        <div className="space-y-1.5">
          <div className="h-5 w-10 bg-muted rounded" />
          <div className="h-3 w-16 bg-muted rounded" />
        </div>
      </div>
    </div>
  );
}

export function StatsStrip({ stats, isLoading }: StatsStripProps) {
  return (
    <div className="overflow-x-auto pb-1 -mx-1 px-1">
      <div className="flex gap-3">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <StatItemSkeleton key={i} />)
          : stats.map((stat, i) => <StatItem key={i} stat={stat} />)
        }
      </div>
    </div>
  );
}
